import React from 'react';
import { Calendar, Heart, Star, Users } from 'lucide-react';
import { EVENT_CITIES } from '../../utils/taiwanCities';
import { SECTION_THEME } from '../../utils/sectionTheme';

const chipStyle = (isSelected) => ({
  minHeight: '44px',
  padding: '7px 12px',
  color: isSelected ? '#ffffff' : '#526579',
  background: isSelected ? SECTION_THEME.events.accent : '#f8fafc',
  border: isSelected ? `1px solid ${SECTION_THEME.events.accent}` : '1px solid #dbe4ee',
  borderRadius: '999px',
  fontFamily: 'inherit',
  fontSize: '12px',
  fontWeight: '750',
  cursor: 'pointer'
});

const fieldStyle = {
  boxSizing: 'border-box',
  width: '100%',
  minHeight: '44px',
  padding: '9px 11px',
  color: '#334155',
  background: '#f8fafc',
  border: '1px solid #dbe4ee',
  borderRadius: '12px',
  fontFamily: 'inherit',
  fontSize: '13px'
};

const sectionLabelStyle = {
  marginBottom: '8px',
  color: '#526579',
  fontSize: '12px',
  fontWeight: '750'
};

export default function EventFilterPanel({
  categories,
  selectedCategory,
  setSelectedCategory,
  selectedCities,
  setSelectedCities,
  dateStartFilter,
  setDateStartFilter,
  dateEndFilter,
  setDateEndFilter,
  filterType,
  setFilterType,
  activeFilterCount,
  searchQuery,
  onClear,
  isMobile,
  variant = 'inline'
}) {
  const isDrawer = variant === 'drawer';
  const toggleCity = (city) => {
    setSelectedCities(previous => previous.includes(city)
      ? previous.filter(item => item !== city)
      : [...previous, city]);
  };

  return (
    <div data-filter-panel="events" data-variant={variant} style={{ marginBottom: isDrawer ? 0 : '18px', padding: isDrawer ? 0 : '15px', background: isDrawer ? 'transparent' : 'rgba(255,255,255,0.94)', border: isDrawer ? 'none' : '1px solid #dbe4ee', borderRadius: isDrawer ? 0 : '18px', boxShadow: isDrawer ? 'none' : '0 10px 28px rgba(30, 64, 99, 0.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: isDrawer ? 'flex-end' : 'space-between', gap: '12px', marginBottom: '12px' }}>
        {!isDrawer && <span style={{ color: '#334155', fontSize: '13px', fontWeight: '850' }}>活動篩選</span>}
        {(activeFilterCount > 0 || searchQuery) && (
          <button type="button" onClick={onClear} style={{ minHeight: '44px', padding: '6px 8px', color: SECTION_THEME.events.accent, background: 'transparent', border: 'none', fontFamily: 'inherit', fontSize: '12px', fontWeight: '800', cursor: 'pointer' }}>
            清除全部
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gap: '17px' }}>
        <div>
          <div style={sectionLabelStyle}>活動類別</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {[{ value: '', label: '全部' }, ...categories.map(category => ({ value: category, label: category }))].map(option => (
              <button key={option.value || 'all'} type="button" onClick={() => setSelectedCategory(option.value)} style={chipStyle(selectedCategory === option.value)}>
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div style={sectionLabelStyle}>活動縣市</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: isMobile ? '118px' : 'none', overflowY: isMobile ? 'auto' : 'visible' }}>
            <button type="button" onClick={() => setSelectedCities([])} style={chipStyle(selectedCities.length === 0)}>全部縣市</button>
            {EVENT_CITIES.map(city => (
              <button key={city} type="button" aria-pressed={selectedCities.includes(city)} onClick={() => toggleCity(city)} style={chipStyle(selectedCities.includes(city))}>
                {city}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div style={sectionLabelStyle}>活動日期區間</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
            <label htmlFor="event-filter-start" style={{ display: 'grid', gap: '6px', color: '#526579', fontSize: '11px', fontWeight: '700' }}>
              開始日期
              <input id="event-filter-start" type="date" aria-label="活動篩選開始日期" value={dateStartFilter} max={dateEndFilter || undefined} onChange={event => setDateStartFilter(event.target.value)} style={fieldStyle} />
            </label>
            <label htmlFor="event-filter-end" style={{ display: 'grid', gap: '6px', color: '#526579', fontSize: '11px', fontWeight: '700' }}>
              結束日期
              <input id="event-filter-end" type="date" aria-label="活動篩選結束日期" value={dateEndFilter} min={dateStartFilter || undefined} onChange={event => setDateEndFilter(event.target.value)} style={fieldStyle} />
            </label>
          </div>
        </div>

        <div>
          <div style={sectionLabelStyle}>更多條件</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <button type="button" aria-pressed={filterType === 'organized'} onClick={() => setFilterType(filterType === 'organized' ? '' : 'organized')} style={{ ...chipStyle(filterType === 'organized'), display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <Calendar size={13} />我發起的活動
            </button>
            <button type="button" aria-pressed={filterType === 'favorites'} onClick={() => setFilterType(filterType === 'favorites' ? '' : 'favorites')} style={{ ...chipStyle(filterType === 'favorites'), display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <Star size={13} fill={filterType === 'favorites' ? 'currentColor' : 'none'} />收藏清單
            </button>
            <button type="button" aria-pressed={filterType === 'joined'} onClick={() => setFilterType(filterType === 'joined' ? '' : 'joined')} style={{ ...chipStyle(filterType === 'joined'), display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <Users size={13} />參加中
            </button>
            <button type="button" aria-pressed={filterType === 'friends'} onClick={() => setFilterType(filterType === 'friends' ? '' : 'friends')} style={{ ...chipStyle(filterType === 'friends'), display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <Heart size={13} fill={filterType === 'friends' ? 'currentColor' : 'none'} />好友活動
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
